/** BTM technology presentation shared by HTML and Word export.
 * Land uses the existing nameplate-sized planning model, never a default load.
 */
import type { SiteRagReport } from '../types/siteRag';
import type { TechnologyId } from './finDecision/types';
import {
  correctedLcoeForBucket,
  resolveReportCorrectedLcoe,
  technologyLabelToBucket,
} from './finDecision/reportLcoeSurface';
import { formatApproxLcoePerMwh, formatApproxLcoeRange } from './lcoeDisplay';
import { describeLcoeDollarBasis } from './lcoe/lcoeExhibitText';
import { isIntentFieldExplicitlyUnknown } from './intent/explicitUnknown';
import { resolveIntentPowerStack } from './powerStack';
import { resolveGenerationLandAcresForTech, resolveGenerationNameplateForTech } from './phaseFeasibility';

type BtmLandReport = Partial<Pick<SiteRagReport, 'projectIntentAtGen' | 'rerunConfig'>>;

/** Immutable generation snapshot wins even when it is explicitly incomplete. */
export function resolveBtmLandBasis(report: BtmLandReport): { facilityMW: number | null; description: string } {
  const intent = report.projectIntentAtGen ?? report.rerunConfig?.projectIntent;
  const unavailable = (reason: string) => ({ facilityMW: null,
    description: `Generation land not quantified: ${reason}. No default demand or bucket midpoint is substituted.` });
  if (!intent) return unavailable('frozen project demand is unavailable');
  if (isIntentFieldExplicitlyUnknown(intent, 'powerBasis')
    || (intent.powerBasis !== 'facility' && intent.powerBasis !== 'it-load')) {
    return unavailable('the frozen demand basis (IT versus facility) is unresolved');
  }
  if (isIntentFieldExplicitlyUnknown(intent, 'powerRequirementMW')
    || isIntentFieldExplicitlyUnknown(intent, 'powerRequirement')) return unavailable('the frozen demand is explicitly unknown');
  const stack = resolveIntentPowerStack(intent);
  if (intent.powerBasis === 'it-load' && (isIntentFieldExplicitlyUnknown(intent, 'pueValue')
    || (stack.pueSource === 'design-target' && isIntentFieldExplicitlyUnknown(intent, 'pueTarget'))
    || !['user', 'design-target'].includes(stack.pueSource))) {
    return unavailable('IT-to-facility conversion needs a declared PUE or design target');
  }
  if (stack.facilityMW == null || !Number.isFinite(stack.facilityMW) || stack.facilityMW <= 0) {
    return unavailable('no exact whole-project facility load resolves from the frozen intent');
  }
  const conversion = intent.powerBasis === 'it-load'
    ? `, converted from IT load at ${stack.pue} PUE (${stack.pueSource})` : '';
  return { facilityMW: stack.facilityMW,
    description: `Planning comparison at ${formatMw(stack.facilityMW)} MW whole-project facility load${conversion}, from the frozen report intent. Each row sizes its own generation nameplate using planning defaults, not an approved plant design. Wind is permanent footprint only, not turbine-array spacing. Setbacks, utilities, data-center land and usable-area diligence remain separate; gross site area does not establish fit.` };
}

function formatMw(mw: number): string { return String(Number(mw.toFixed(2))); }

/** Multi-technology labels do not identify a single generation footprint. */
function landTechnology(label: string): TechnologyId | null {
  const t = label.toLowerCase();
  const matches: TechnologyId[] = [];
  if (t.includes('nuclear') || t.includes('smr')) matches.push('nuclear-smr');
  if (t.includes('gas')) matches.push('gas-cc');
  if (t.includes('solar')) matches.push('solar-bess');
  if (t.includes('wind')) matches.push('wind-bess');
  return matches.length === 1 ? matches[0]! : null;
}

export function deriveBtmLandCell(technology: string, report: BtmLandReport): {
  acres: number | null; nameplateMW: number | null; text: string;
} {
  const unknown = { acres: null, nameplateMW: null, text: 'Not quantified' };
  const { facilityMW } = resolveBtmLandBasis(report);
  const tech = landTechnology(technology);
  if (facilityMW == null || tech == null) return unknown;
  const acres = resolveGenerationLandAcresForTech(facilityMW, tech);
  const nameplateMW = resolveGenerationNameplateForTech(facilityMW, tech);
  if (acres == null || !Number.isFinite(nameplateMW) || nameplateMW <= 0) return unknown;
  return { acres, nameplateMW, text: `${Math.ceil(acres)} ac (${formatMw(nameplateMW)} MW nameplate)` };
}

export interface BtmTechRow {
  /** Display name from BtmOption.technology (passed through unchanged). */
  technology: string;
  /** Risk tier string ("Red" / "Amber" / "Green") — derived from
   *  BtmOption.riskLevel for in-app/DOCX consistency. */
  riskTier: 'Red' | 'Amber' | 'Green';
  /** Reported $/MWh with its basis qualification, or a dash when unavailable. */
  lcoe: string;
  /** Timeline range like "5-7 yr" or "1-2 yr". */
  timeline: string;
  /** Planning footprint and modeled generation nameplate, or not quantified. */
  land: string;
  /** CO₂ intensity — "Zero" for zero-carbon techs, a g/kWh band for
   *  combustion, "—" if unknown. */
  co2: string;
  /** Whether this tech is viable for the site given its constraints
   *  — drives the in-app row-opacity dim. Renderers may choose to
   *  dim/strike-through; the DOCX currently doesn't styled-dim
   *  rows, but the flag is available. */
  viable: boolean | null;
}

const RISK_TO_TIER: Record<'High' | 'Medium' | 'Low', 'Red' | 'Amber' | 'Green'> = {
  High: 'Red',
  Medium: 'Amber',
  Low: 'Green',
};

/** One policy for technology-table cells in HTML/PDF and editable exports.
 * Geography cannot establish project costs. Retain saved/model values as
 * reported, disclose their basis, and never substitute a catalog range. */
export function deriveBtmLcoeCell(technology: string, report: Partial<SiteRagReport>): string {
  const surface = resolveReportCorrectedLcoe(report as SiteRagReport);
  const entry = correctedLcoeForBucket(surface, technologyLabelToBucket(technology));
  if (entry.midUsdPerMwh == null) return '—';
  const low = Math.round(entry.lowUsdPerMwh ?? entry.midUsdPerMwh);
  const high = Math.round(entry.highUsdPerMwh ?? entry.midUsdPerMwh);
  const value = low < high ? formatApproxLcoeRange(low, high) : formatApproxLcoePerMwh(entry.midUsdPerMwh);
  const dollarBasis = describeLcoeDollarBasis(entry);
  if (entry.basis === 'benchmark-pathway') {
    return `${value} — ${dollarBasis}; benchmark assumptions, not a verified site estimate`;
  }
  const incentives = entry.subsidySummary
    ?? (entry.subsidyStatus === 'unsubsidized' ? 'without incentive credits' : 'incentive basis unknown');
  return `${value} — saved model: ${entry.currencyYear ?? 'currency year unknown'}, ${dollarBasis}; ${incentives}; ${entry.firmingBasis ?? 'firming basis unknown'}; ${entry.serviceScope ?? 'service scope unknown'}`;
}

/** BTM planning characteristics, with source-bound LCOE presentation. */
export function deriveBtmTechRow(
  technology: string,
  riskLevel: 'High' | 'Medium' | 'Low',
  report: Partial<SiteRagReport>,
): BtmTechRow {
  const wCF = report.datasets?.wind?.capacityFactor;
  const siteAcres = typeof report.siteArea === 'number' && Number.isFinite(report.siteArea) && report.siteArea > 0 ? report.siteArea : null;

  const land = deriveBtmLandCell(technology, report);

  const t = technology.toLowerCase();
  const riskTier = RISK_TO_TIER[riskLevel];

  const lcoe = deriveBtmLcoeCell(technology, report);

  if (t.includes('nuclear') || t.includes('smr')) {
    return {
      technology,
      riskTier,
      lcoe,
      timeline: '5-7 yr',
      land: land.text,
      co2: 'Zero',
      viable: true,
    };
  }
  if (t.includes('gas')) {
    return {
      technology,
      riskTier,
      lcoe,
      timeline: '~18–36+ mo (lateral & permits)',
      land: land.text,
      co2: '350-500 kg',
      viable: true,
    };
  }
  if (t.includes('solar')) {
    return {
      technology,
      riskTier,
      lcoe,
      timeline: '1-2 yr',
      land: land.text,
      co2: 'Zero',
      viable: land.acres != null && siteAcres != null && siteAcres < land.acres ? false : null,
    };
  }
  if (t.includes('wind')) {
    return {
      technology,
      riskTier,
      lcoe,
      timeline: '1-3 yr',
      land: land.text,
      co2: 'Zero',
      viable: (wCF ?? 0) > 0.2,
    };
  }
  return { technology, riskTier, lcoe, timeline: '—', land: '—', co2: '—', viable: true };
}

/** Land is in acres; the accompanying disclosure identifies its facility-load
 * denominator and each cell identifies the modeled generation nameplate. */
export const TECH_OVERVIEW_COLUMNS: ReadonlyArray<{ label: string; widthPct: number }> = [
  { label: 'Technology',   widthPct: 22 },
  { label: 'Risk',         widthPct: 14 },
  { label: 'LCOE',         widthPct: 16 },
  { label: 'Timeline',     widthPct: 14 },
  { label: 'Land (acres)', widthPct: 16 },
  // Plain ASCII is intentional for DOCX/PPTX export: the HTML report uses a
  // styled ASCII subscript, while U+2082 is not present in every export font.
  { label: 'CO2',          widthPct: 18 },
];

/**
 * Redhead review wave 3 (defect 8) — a column whose EVERY body cell is an
 * empty/dash placeholder ("–", "—", "-", "", "n/a", "pending") carries no
 * information and reads as an unfinished deliverable; drop it from the
 * rendered table instead of shipping a column of dashes. (E.g. the tech
 * comparison's LCOE column when every project-specific LCOE is pending.)
 */
const EMPTY_TABLE_CELL_RE = /^\s*(?:|–|—|-|n\/a|pending)\s*$/i;

export function isEmptyTableCell(cell: unknown): boolean {
  return typeof cell === 'string' && EMPTY_TABLE_CELL_RE.test(cell);
}

/**
 * Drop table columns whose every body cell is empty/dash. Generic over the
 * column descriptor + cell types so the React table (JSX cells), the DOCX
 * serializer ({label,widthPct} columns) and the PPTX serializer (string
 * columns) all share ONE emptiness rule. Non-string cells (JSX badges,
 * numbers) count as non-empty. Zero-row tables return unchanged (nothing to
 * judge emptiness against).
 */
export function dropAllEmptyColumns<C, T>(
  columns: ReadonlyArray<C>,
  rows: ReadonlyArray<ReadonlyArray<T>>,
): { columns: C[]; rows: T[][] } {
  if (rows.length === 0) {
    return { columns: [...columns], rows: rows.map((r) => [...r]) };
  }
  const keep = columns.map((_, colIdx) =>
    rows.some((row) => !isEmptyTableCell(row[colIdx])),
  );
  if (keep.every(Boolean)) {
    return { columns: [...columns], rows: rows.map((r) => [...r]) };
  }
  return {
    columns: columns.filter((_, i) => keep[i]),
    rows: rows.map((row) => row.filter((_, i) => keep[i])),
  };
}
